import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  NodeTypes,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlowProvider,
  ReactFlowInstance,
  Panel,
  ConnectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css'; 
import { Workflow, WorkflowStep } from '../types/workflow';
import { STEP_TYPE_TEMPLATES, CONSTANT_STEPS } from '../constants/stepTemplates';
import { AI_PROVIDERS } from '../constants/aiProviders';
import N8nBasicInfoNode from './n8n-nodes/N8nBasicInfoNode';
import N8nDAppConfigNode from './n8n-nodes/N8nDAppConfigNode';
import N8nWorkflowStack from './n8n-nodes/N8nWorkflowStack';
import N8nSystemStack from './n8n-nodes/N8nSystemStack';
import N8nConstructNode from './n8n-nodes/N8nConstructNode';
import N8nNodePanel from './n8n-nodes/N8nNodePanel';
import StepEditor from './StepEditor';
import { Plus, Zap, Settings, X } from 'lucide-react';
import { slugify } from '../utils/slug';

interface N8nStyleCanvasProps {
  workflow: Workflow;
  setWorkflow: React.Dispatch<React.SetStateAction<Workflow>>;
}

const nodeTypes: NodeTypes = {
  basicInfo: N8nBasicInfoNode,
  dappConfig: N8nDAppConfigNode,
  workflowStack: N8nWorkflowStack,
  systemStack: N8nSystemStack,
  constructNode: N8nConstructNode,
};

// Sharp SaaS connection line
const connectionLineStyle = {
  strokeWidth: 2.5,
  stroke: '#6366f1',
};

const POSITIONS_STORAGE_KEY = 'n8n-workflow-positions';

const N8nStyleCanvas: React.FC<N8nStyleCanvasProps> = ({ workflow, setWorkflow }) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const onInit = useCallback((reactFlowInstance: ReactFlowInstance) => {
    setReactFlowInstance(reactFlowInstance);
    // Auto fit view when canvas loads
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.1, duration: 800 });
    }, 100);
  }, []);
  const [showNodePanel, setShowNodePanel] = useState(false);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null);
  const [editingOriginalName, setEditingOriginalName] = useState<string | null>(null);
  const [showBasicInfoEditor, setShowBasicInfoEditor] = useState(false);
  const [showDAppConfigEditor, setShowDAppConfigEditor] = useState(false);
  // Load positions from localStorage immediately (synchronously)
  const loadSavedPositions = (): Record<string, { x: number; y: number }> => {
    try {
      const savedPositions = localStorage.getItem(POSITIONS_STORAGE_KEY);
      return savedPositions ? JSON.parse(savedPositions) : {};
    } catch (error) {
      console.warn('Failed to load node positions:', error);
      return {};
    }
  };

  const [nodePositions, setNodePositions] = useState<Record<string, { x: number; y: number }>>(loadSavedPositions);
  
  // Helper to identify construct-evm nodes properly
  const isConstructEvmNode = useCallback((step: any): boolean => {
    return (
      step.image === 'ghcr.io/krnl-labs/executor-encoder-evm@sha256:b28823d12eb1b16cbcc34c751302cd2dbe7e35480a5bc20e4e7ad50a059b6611' &&
      step.next === 'prepare-authdata'
    );
  }, []);

  // Build a safe, normalized workflow object to guard against malformed saved data
  const safeWorkflow = React.useMemo(() => {
    const steps = Array.isArray(workflow?.workflow?.steps) ? workflow.workflow.steps : [];
    return {
      ...workflow,
      workflow: {
        name: workflow?.workflow?.name || '',
        version: workflow?.workflow?.version || 'v1.0.0',
        steps,
      },
      target: {
        contract: workflow?.target?.contract || '',
        function: workflow?.target?.function || '',
        authData_result: typeof workflow?.target?.authData_result !== 'undefined' ? workflow.target.authData_result : '0x00',
        parameters: Array.isArray(workflow?.target?.parameters) ? workflow.target.parameters : [],
      },
      intent: workflow?.intent || { id: '', signature: '', deadline: '' },
    } as Workflow;
  }, [workflow]);

  // Sync editingStep with workflow state (for undo/redo support)
  useEffect(() => {
    if (editingStep && editingOriginalName) {
      // Find the current version of the editing step in the workflow
      const currentStep = safeWorkflow.workflow.steps.find(
        step => step.name === editingOriginalName
      );
      
      // If found and different, update editingStep
      if (currentStep && JSON.stringify(currentStep) !== JSON.stringify(editingStep)) {
        setEditingStep(currentStep);
        // Update original name if step was renamed
        if (currentStep.name !== editingOriginalName) {
          setEditingOriginalName(currentStep.name);
        }
      }
      
      // If step was deleted via undo, close the editor
      if (!currentStep) {
        setShowStepEditor(false);
        setEditingStep(null);
        setEditingOriginalName(null);
      }
    }
  }, [safeWorkflow, editingStep, editingOriginalName]);
  
  // Get custom steps, construct step, and system steps (excluding construct from system stack)
  const customSteps = safeWorkflow.workflow.steps.filter(step => 
    !CONSTANT_STEPS.some(cs => cs.name === step.name) && !isConstructEvmNode(step)
  );
  const constructStep = safeWorkflow.workflow.steps.find(step => isConstructEvmNode(step)) || null;
  const systemSteps = safeWorkflow.workflow.steps.filter(step => 
    CONSTANT_STEPS.some(cs => cs.name === step.name)
  );

  // Save positions to localStorage whenever they change
  const savePositions = useCallback((positions: Record<string, { x: number; y: number }>) => {
    try {
      localStorage.setItem(POSITIONS_STORAGE_KEY, JSON.stringify(positions));
      setNodePositions(positions);
    } catch (error) {
      console.warn('Failed to save node positions:', error);
    }
  }, []);

  // Define handlers first
  const handleAddNode = useCallback((stepType: string) => {
    let template: WorkflowStep | undefined;

    // Helper: deep clone to avoid shared references between steps
    const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));
    
    // Handle AI provider nodes
    if (stepType.startsWith('AI:')) {
      const providerId = stepType.split(':')[1];
      const aiProvider = AI_PROVIDERS.find(p => p.id === providerId);
      if (aiProvider) {
        const cloned = deepClone(aiProvider.template);
        template = {
          ...cloned,
          name: `${aiProvider.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`,
        };
      }
    } else if (stepType === 'BLANK') {
      // Handle blank/custom node
      template = {
        type: 10,
        name: `custom-step-${Date.now()}`,
        image: 'ghcr.io/krnl-labs/executor-http@sha256:07ef35b261014304a0163502a7f1dec5395c5cac1fc381dc1f79b052389ab0d5',
        attestor: '{{ENV.ATTESTOR_IMAGE}}',
        next: 'prepare-authdata',
        config: {},
        inputs: [],
        outputs: []
      };
    } else {
      // Handle regular node types
      const base = STEP_TYPE_TEMPLATES[stepType as keyof typeof STEP_TYPE_TEMPLATES];
      if (base) {
        const cloned = deepClone(base);
        // Ensure unique name (avoid collisions like multiple 'api-step')
        template = {
          ...cloned,
          name: `${cloned.name}-${Date.now()}`,
        };
      }
    }
    
    if (!template) return;

    const newStep: WorkflowStep = deepClone(template);

    // Add to workflow and auto-manage construct-evm
    setWorkflow(prev => {
      const prevSteps = Array.isArray(prev?.workflow?.steps) ? prev.workflow.steps : [];
      const prevCustom = prevSteps.filter(s => !CONSTANT_STEPS.some(cs => cs.name === s.name) && !isConstructEvmNode(s));
      const hadNoCustom = prevCustom.length === 0;

      // Ensure last custom points correctly
      const updatedSteps = [...prevSteps, newStep];

      let stepsWithConstruct = updatedSteps;
      if (hadNoCustom) {
        // First custom step - add construct-evm and system steps
        const constructNode: WorkflowStep = {
          name: 'construct-evm',
          image: 'ghcr.io/krnl-labs/executor-encoder-evm@sha256:b28823d12eb1b16cbcc34c751302cd2dbe7e35480a5bc20e4e7ad50a059b6611',
          attestor: '{{ENV.ATTESTOR_IMAGE}}',
          next: 'prepare-authdata',
          config: { parameters: [] as any[] },
          inputs: { value: {} as Record<string, any> } as any,
          outputs: [ { name: 'result', value: 'result', required: true, export: true } ] as any,
        } as any;

        // Ensure newStep points to construct-evm
        stepsWithConstruct = updatedSteps.map(s => s === newStep ? { ...s, next: 'construct-evm' } as any : s);
        
        // Add only construct node (system steps added later when needed)
        stepsWithConstruct = [...stepsWithConstruct, constructNode];
      } else {
        // When adding subsequent custom steps, make previous last custom point to the new one, and new last to construct-evm if exists
        const existsConstruct = prevSteps.some(s => isConstructEvmNode(s));
        const prevLast = prevCustom[prevCustom.length - 1];
        stepsWithConstruct = stepsWithConstruct = updatedSteps.map(s => {
          if (s.name === prevLast.name) return { ...s, next: newStep.name } as any;
          return s;
        });
        if (existsConstruct) {
          stepsWithConstruct = stepsWithConstruct.map(s => s === newStep ? { ...s, next: 'construct-evm' } as any : s);
        }
      }

      return {
        ...prev,
        workflow: {
          ...(prev.workflow || { name: '', version: 'v1.0.0', steps: [] }),
          steps: stepsWithConstruct,
        }
      };
    });
    
    // Close the node panel after adding
    setShowNodePanel(false);
  }, [setWorkflow]);

  const handleDeleteNode = useCallback((nodeId: string) => {
    setWorkflow(prev => {
      const prevSteps = Array.isArray(prev?.workflow?.steps) ? prev.workflow.steps : [];
      let remaining = prevSteps.filter(step => step.name !== nodeId);
      const remainingCustom = remaining.filter(s => !CONSTANT_STEPS.some(cs => cs.name === s.name) && !isConstructEvmNode(s));
      let steps = remaining;
      if (remainingCustom.length === 0) {
        // Remove construct-evm if no custom steps left
        steps = steps.filter(s => !isConstructEvmNode(s));
        // Reset prepare-authdata result to default
        steps = steps.map(s => {
          if (s.name === 'prepare-authdata') {
            const inputsObj = Array.isArray((s as any).inputs)
              ? Object.fromEntries(((s as any).inputs as any[]).map((i: any) => [i.name, i.value]))
              : ((s as any).inputs || {});
            return { ...s, inputs: { ...inputsObj, result: '0x00', executions: [] } } as any;
          }
          return s;
        });
      } else {
        // Ensure last custom points to construct-evm if present, otherwise to prepare-authdata
        const existsConstruct = steps.some(s => isConstructEvmNode(s));
        const lastCustom = remainingCustom[remainingCustom.length - 1];
        steps = steps.map(s => {
          if (s.name === lastCustom.name) return { ...s, next: existsConstruct ? 'construct-evm' : 'prepare-authdata' } as any;
          return s;
        });
      }
      return {
        ...prev,
        workflow: { ...(prev.workflow || { name: '', version: 'v1.0.0', steps: [] }), steps }
      };
    });
    // If currently editing this node, close the editor
    if (editingStep?.name === nodeId) {
      setShowStepEditor(false);
      setEditingStep(null);
      setEditingOriginalName(null);
    }
  }, [setWorkflow, editingStep]);

  const handleEditStep = useCallback((step: WorkflowStep) => {
    setEditingStep(step);
    setEditingOriginalName(step.name);
    setShowStepEditor(true);
  }, []);

  const handleStepUpdate = useCallback((updatedStep: WorkflowStep) => {
    setWorkflow(prev => ({
      ...prev,
      workflow: {
        ...(prev.workflow || { name: '', version: 'v1.0.0', steps: [] }),
        steps: (Array.isArray(prev?.workflow?.steps) ? prev.workflow.steps : []).map(step => {
          const targetName = editingOriginalName || updatedStep.name;
          // Update the step itself
          if (step.name === targetName) {
            return updatedStep;
          }
          // Update next field references if name changed
          if (editingOriginalName && editingOriginalName !== updatedStep.name && step.next === editingOriginalName) {
            return { ...step, next: updatedStep.name };
          }
          return step;
        })
      }
    }));
    // Don't manually update editingStep - let the useEffect sync it from workflow
    // This ensures undo/redo works correctly with the step editor open
  }, [setWorkflow, editingOriginalName]);

  const handleEditBasicInfo = useCallback(() => {
    setShowBasicInfoEditor(true);
  }, []);

  const handleEditDAppConfig = useCallback(() => {
    setShowDAppConfigEditor(true);
  }, []);

  const handleBasicInfoUpdate = useCallback((field: string, value: any) => {
    setWorkflow(prev => ({
      ...prev,
      [field]: value
    }));
  }, [setWorkflow]);

  const handleWorkflowInfoUpdate = useCallback((field: string, value: any) => {
    const newValue = field === 'name' ? slugify(String(value)) : value;
    setWorkflow(prev => ({
      ...prev,
      workflow: {
        ...(prev.workflow || { name: '', version: 'v1.0.0', steps: [] }),
        [field]: newValue
      }
    }));
  }, [setWorkflow]);

  // New: update top-level intent fields
  const handleIntentUpdate = useCallback((field: string, value: any) => {
    setWorkflow(prev => ({
      ...prev,
      intent: {
        ...(prev.intent || { id: '', signature: '', deadline: '' }),
        [field]: value
      }
    }));
  }, [setWorkflow]);

  // New: update target fields
  const handleTargetUpdate = useCallback((field: string, value: any) => {
    setWorkflow(prev => {
      // Auto-determine authData_result based on construct-evm step existence
      const hasConstructEvmStep = (Array.isArray(prev?.workflow?.steps) ? prev.workflow.steps : []).some(step => isConstructEvmNode(step));

      const autoAuthDataResult = hasConstructEvmStep
        ? '${construct-evm.result}'  // With construct-evm step: reference its result
        : '0x00';                    // Without construct-evm step: empty bytes

      return {
        ...prev,
        target: {
          ...prev.target,
          contract: prev.target?.contract || '',
          function: prev.target?.function || '',
          authData_result: autoAuthDataResult,
          parameters: prev.target?.parameters || [],
          [field]: value
        }
      };
    });
  }, [setWorkflow]);

  // Auto-detect parameter type from value
  const detectParameterType = (value: string): string => {
    if (!value) return 'string';
    
    // Check for variable references
    if (value.includes('${') || value.includes('{{')) return 'string';
    
    // Check for address (0x followed by 40 hex chars)
    if (/^0x[a-fA-F0-9]{40}$/.test(value)) return 'address';
    
    // Check for bytes (0x followed by hex chars)
    if (/^0x[a-fA-F0-9]+$/.test(value)) return 'bytes';
    
    // Check for boolean
    if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') return 'bool';
    
    // Check for number (uint256)
    if (/^\d+$/.test(value)) return 'uint256';
    
    return 'string';
  };

  // Check if a parameter type matches the expected authData tuple structure
  const isAuthDataTuple = (paramType: string): boolean => {
    // The expected authData pattern
    const authDataPattern = /^\(uint256,uint256,bytes32,\(bytes32,bytes,bytes\)\[\],bytes,bool,bytes\)$/;
    return authDataPattern.test(paramType);
  };

  // Parse function signature to extract parameters beyond AuthData
  const parseExtraParameters = (functionSig: string): Array<{name: string, type: string, value: string}> => {
    if (!functionSig) return [];

    try {
      // Extract parameters from function signature
      // Example: "submitData((uint256,uint256,bytes32,(bytes32,bytes,bytes)[],bytes,bool,bytes),uint256,address)"
      const match = functionSig.match(/\((.+)\)$/);
      if (!match) return [];

      const params = match[1];
      const paramTypes: string[] = [];
      let depth = 0;
      let current = '';

      // Parse parameters handling nested tuples
      for (let i = 0; i < params.length; i++) {
        const char = params[i];
        if (char === '(' || char === '[') {
          depth++;
          current += char;
        } else if (char === ')' || char === ']') {
          depth--;
          current += char;
        } else if (char === ',' && depth === 0) {
          if (current.trim()) paramTypes.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      if (current.trim()) paramTypes.push(current.trim());

      // Check if first parameter is AuthData and extract parameters after it
      const extraParams = paramTypes.length > 0 && isAuthDataTuple(paramTypes[0])
        ? paramTypes.slice(1)  // Skip the first AuthData parameter
        : paramTypes;          // If no AuthData, all parameters are extra

      return extraParams.map((type, index) => ({
        name: `param${index + 1}`,
        type: type.includes('uint') ? 'uint256' :
              type.includes('address') ? 'address' :
              type.includes('bool') ? 'bool' :
              type.includes('bytes') ? 'bytes' : 'string',
        value: ''
      }));
    } catch (error) {
      console.warn('Failed to parse function signature:', error);
      return [];
    }
  };

  // Auto-fill parameters when function signature changes
  const handleFunctionSignatureChange = (newSig: string) => {
    handleTargetUpdate('function', newSig);

    // Auto-generate parameters
    const extraParams = parseExtraParameters(newSig);
    if (extraParams.length > 0) {
      handleTargetUpdate('parameters', extraParams);
    }
  };

  // Auto-detect parameters when function signature is loaded from project
  useEffect(() => {
    const functionSig = (workflow as any)?.dapp?.targetFunctionSignature;
    if (functionSig && functionSig.trim()) {
      const extraParams = parseExtraParameters(functionSig);
      if (extraParams.length > 0) {
        handleTargetUpdate('parameters', extraParams);
      }
    }
  }, [(workflow as any)?.dapp?.targetFunctionSignature]);


  const handleDAppConfigUpdate = useCallback((field: string, value: any) => {
    const newValue = field === 'name' ? slugify(String(value)) : value;
    setWorkflow(prev => ({
      ...prev,
      dapp: {
        ...(prev.dapp || { name: '', targetChainID: 1, targetContractAddress: '', targetFunctionSignature: '' }),
        // Always sync dapp name with workflow name
        name: prev.workflow.name,
        [field]: newValue
      }
    }));
  }, [setWorkflow]);

  const handleReorderSteps = useCallback((reorderedCustomSteps: WorkflowStep[]) => {
    setWorkflow(prev => {
      const prevSteps = Array.isArray(prev?.workflow?.steps) ? prev.workflow.steps : [];
      const systemStepsLocal = prevSteps.filter(step => 
        CONSTANT_STEPS.some(cs => cs.name === step.name) || isConstructEvmNode(step)
      );
      
      // Update next fields for the reordered workflow steps
      const updatedCustomSteps = reorderedCustomSteps.map((step, index) => ({
        ...step,
        next: index < reorderedCustomSteps.length - 1 
          ? reorderedCustomSteps[index + 1].name 
          : (systemStepsLocal.some(s => isConstructEvmNode(s))
              ? 'construct-evm'
              : (systemStepsLocal.length > 0 ? 'prepare-authdata' : undefined))
      }));
      
      return {
        ...prev,
        workflow: {
          ...(prev.workflow || { name: '', version: 'v1.0.0', steps: [] }),
          steps: [...updatedCustomSteps, ...systemStepsLocal]
        }
      };
    });
  }, [setWorkflow]);

  // Create initial nodes with stack-based layout
  const createInitialNodes = (): Node[] => {
    const nodes: Node[] = [];
    let xPosition = 240;
    const yPosition = 300;
    const nodeSpacing = 300;

    // 1. Basic Info Node (always first)
    const basicInfoPos = nodePositions['basic-info'] || { x: xPosition, y: yPosition };
    nodes.push({
      id: 'basic-info',
      type: 'basicInfo',
      position: basicInfoPos,
      data: { workflow: safeWorkflow, onEditBasicInfo: handleEditBasicInfo },
      draggable: true,
    });
    xPosition += nodeSpacing;

    // 2. dApp Config Node
    const dappConfigPos = nodePositions['dapp-config'] || { x: xPosition, y: yPosition };
    nodes.push({
      id: 'dapp-config',
      type: 'dappConfig',
      position: dappConfigPos,
      data: { workflow: safeWorkflow, onEditDAppConfig: handleEditDAppConfig },
      draggable: true,
    });
    xPosition += nodeSpacing;

    // 3. Workflow Stack (replaces individual workflow nodes)
    const workflowStackPos = nodePositions['workflow-stack'] || { x: xPosition, y: yPosition - 50 };
    nodes.push({
      id: 'workflow-stack',
      type: 'workflowStack',
      position: workflowStackPos,
      data: {
        customSteps,
        onAddNode: handleAddNode,
        onShowNodePanel: () => setShowNodePanel(true),
        onEditStep: handleEditStep,
        onDeleteStep: handleDeleteNode,
        onReorderSteps: handleReorderSteps,
      },
      draggable: true,
      dragHandle: '.workflow-stack-drag-handle', // Only allow dragging from specific handle
    });
    xPosition += nodeSpacing;

    // 4. Construct EVM node (if present), sits between workflow and system
    if (constructStep) {
      const constructPos = nodePositions['construct-node'] || { x: xPosition, y: yPosition - 50 };
      nodes.push({
        id: 'construct-node',
        type: 'constructNode',
        position: constructPos,
        data: {
          constructStep,
          onEditStep: handleEditStep,
        },
        draggable: true,
      });
      xPosition += nodeSpacing;
    }

    // 5. System Stack (replaces individual system nodes)
    if (systemSteps.length > 0) {
      const systemStackPos = nodePositions['system-stack'] || { x: xPosition, y: yPosition - 50 };
      nodes.push({
        id: 'system-stack',
        type: 'systemStack',
        position: systemStackPos,
        data: {
          systemSteps,
          onEditStep: handleEditStep,
        },
        draggable: true,
      });
    }

    return nodes;
  };

  // Create initial edges with stack-to-stack connections
  const createInitialEdges = (): Edge[] => {
    const edges: Edge[] = [];

    // Connect basic-info to dapp-config
    edges.push({
      id: 'basic-to-dapp',
      source: 'basic-info',
      target: 'dapp-config',
      type: 'smoothstep',
      animated: false,
      style: { strokeWidth: 2.5, stroke: '#6366f1' },
    });

    // Connect dapp-config to workflow stack
    edges.push({
      id: 'dapp-to-workflow-stack',
      source: 'dapp-config',
      target: 'workflow-stack',
      type: 'smoothstep',
      animated: false,
      style: { strokeWidth: 2.5, stroke: '#6366f1' },
    });

    // Connect workflow stack -> construct (if exists) -> system stack
    if (constructStep) {
      edges.push({
        id: 'workflow-stack-to-construct',
        source: 'workflow-stack',
        target: 'construct-node',
        type: 'smoothstep',
        animated: false,
        style: { strokeWidth: 2.5, stroke: '#6366f1' },
      });
      if (systemSteps.length > 0) {
        edges.push({
          id: 'construct-to-system-stack',
          source: 'construct-node',
          target: 'system-stack',
          type: 'smoothstep',
          animated: false,
          style: { strokeWidth: 2.5, stroke: '#6366f1' },
        });
      }
    } else if (systemSteps.length > 0) {
      edges.push({
        id: 'workflow-stack-to-system-stack',
        source: 'workflow-stack',
        target: 'system-stack',
        type: 'smoothstep',
        animated: false,
        style: { strokeWidth: 2.5, stroke: '#6366f1' },
      });
    }

    return edges;
  };

  const [nodes, setNodes, onNodesChange] = useNodesState(createInitialNodes());
  const [edges, setEdges, onEdgesChange] = useEdgesState(createInitialEdges());

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge({ ...params, type: 'smoothstep' }, eds)),
    [setEdges]
  );

  // Handle node drag end to save positions
  const onNodeDragStop = useCallback((_: React.MouseEvent, node: Node) => {
    const newPositions = { ...nodePositions, [node.id]: node.position };
    savePositions(newPositions);
  }, [nodePositions, savePositions]);

  // Handle nodes change to update positions
  const handleNodesChange = useCallback((changes: any[]) => {
    onNodesChange(changes);
    
    // Extract position updates from changes
    const positionChanges = changes.filter(change => 
      change.type === 'position' && change.position && change.dragging === false
    );
    
    if (positionChanges.length > 0) {
      const newPositions = { ...nodePositions };
      positionChanges.forEach(change => {
        if (change.position) {
          newPositions[change.id] = change.position;
        }
      });
      savePositions(newPositions);
    }
  }, [onNodesChange, nodePositions, savePositions]);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    if (!reactFlowWrapper.current || !reactFlowInstance) return;

    const type = event.dataTransfer.getData('application/reactflow');
    
    if (!type) return;

    handleAddNode(type);
  }, [reactFlowInstance, handleAddNode]);

  // Update nodes and edges when workflow changes
  React.useEffect(() => {
    const newNodes = createInitialNodes();
    const newEdges = createInitialEdges();
    
    // Only update if there are actual changes to prevent unnecessary re-renders
    setNodes(prevNodes => {
      if (JSON.stringify(prevNodes.map(n => n.id).sort()) !== JSON.stringify(newNodes.map(n => n.id).sort())) {
        return newNodes;
      }
      // Update data for existing nodes without changing positions
      return prevNodes.map(prevNode => {
        const newNode = newNodes.find(n => n.id === prevNode.id);
        return newNode ? { ...newNode, position: prevNode.position } : prevNode;
      });
    });
    
    setEdges(newEdges);
  }, [workflow]);

  // Ensure prepare-authdata consumes construct-evm.result when construct is present
  React.useEffect(() => {
    setWorkflow(prev => {
      const prevSteps = Array.isArray(prev?.workflow?.steps) ? prev.workflow.steps : [];
      const hasConstruct = prevSteps.some(s => isConstructEvmNode(s));
      let changed = false;
      const updated = prevSteps.map(s => {
        if (s.name !== 'prepare-authdata') return s;
        const inputsObj = Array.isArray((s as any).inputs)
          ? Object.fromEntries(((s as any).inputs as any[]).map((i: any) => [i.name, i.value]))
          : ((s as any).inputs || {});
        const desiredResult = hasConstruct ? '${construct-evm.result}' : (inputsObj.result ?? '0x00');
        const desiredExecutions = inputsObj.executions ?? [];
        if (inputsObj.result !== desiredResult || inputsObj.executions === undefined) {
          changed = true;
          return { ...s, inputs: { ...inputsObj, result: desiredResult, executions: desiredExecutions } } as any;
        }
        return s;
      });
      if (!changed) return prev;
      return {
        ...prev,
        workflow: { ...(prev.workflow || { name: '', version: 'v1.0.0', steps: [] }), steps: updated }
      };
    });
  }, [safeWorkflow.workflow.steps, setWorkflow]);

  return (
    <div className="w-full h-full" ref={reactFlowWrapper}>
      {/* n8n-style Node Panel */}
      {showNodePanel && (
        <N8nNodePanel onClose={() => setShowNodePanel(false)} />
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onNodeDragStop={onNodeDragStop}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        connectionLineStyle={connectionLineStyle}
        connectionMode={ConnectionMode.Loose}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        minZoom={0.2}
        maxZoom={4}
        attributionPosition="bottom-right"
        className="bg-n8n-dark-950"
        fitView={true}
        fitViewOptions={{ padding: 100 }}
      >
        {/* Sharp SaaS background */}
        <Background 
          variant={BackgroundVariant.Dots} 
          gap={24} 
          size={1.2} 
          color="#374151"
          className="bg-n8n-dark-950"
        />

        {/* Sharp SaaS controls */}
        <Controls 
          className="bg-n8n-dark-800/95 backdrop-blur-sm border border-n8n-dark-600/50 rounded-lg shadow-2xl"
          showZoom={true}
          showFitView={true}
          showInteractive={false}
        />

        {/* Sharp SaaS minimap */}
        <MiniMap 
          className="bg-n8n-dark-800/95 backdrop-blur-sm border border-n8n-dark-600/50 rounded-lg shadow-2xl"
          nodeColor="#6366f1"
          maskColor="rgba(0, 0, 0, 0.8)"
          nodeStrokeWidth={1.5}
          pannable
          zoomable
        />

        {/* Sharp SaaS toolbar */}
        <Panel position="top-center">
          <div className="flex items-center space-x-3 bg-n8n-dark-800/95 backdrop-blur-xl rounded-xl shadow-2xl border border-n8n-dark-600/50 px-6 py-3">
            <button
              onClick={() => setShowNodePanel(true)}
              className="flex items-center space-x-2 bg-gradient-to-r from-n8n-primary-500 to-n8n-primary-600 hover:from-n8n-primary-600 hover:to-n8n-primary-700 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-medium text-sm"
            >
              <Plus size={14} />
              <span>Add Node</span>
            </button>
            <div className="w-px h-5 bg-n8n-dark-600"></div>
            <div className="flex items-center space-x-2 text-xs text-n8n-dark-400">
              <Zap size={12} className="text-n8n-primary-400" />
              <span>Workflow Builder</span>
            </div>
          </div>
        </Panel>

        {/* Sharp workflow info panel */}
        <Panel position="top-left">
          <div className="bg-n8n-dark-800/95 backdrop-blur-xl rounded-lg shadow-2xl border border-n8n-dark-600/50 px-4 py-3">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-2 h-2 bg-n8n-success-400 rounded-full animate-pulse"></div>
              <div className="font-semibold text-n8n-dark-50 text-sm">
                {safeWorkflow.workflow.name || 'Untitled Workflow'}
              </div>
            </div>
            <div className="flex items-center space-x-4 text-xs text-n8n-dark-400">
              <div className="flex items-center space-x-1">
                <Settings size={10} />
                <span>{nodes.length} nodes</span>
              </div>
              <div className="flex items-center space-x-1">
                <Zap size={10} />
                <span>{edges.length} connections</span>
              </div>
            </div>
          </div>
        </Panel>
      </ReactFlow>

      {/* Sharp Step Editor Modal */}
      {showStepEditor && editingStep && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-n8n-dark-800/95 backdrop-blur-xl rounded-xl border border-n8n-dark-600/50 w-full max-w-3xl max-h-[75vh] overflow-hidden shadow-2xl my-4">
            <div className="p-6 border-b border-n8n-dark-600/50 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-n8n-primary-500/20 to-n8n-primary-600/20 rounded-lg">
                  <Settings size={16} className="text-n8n-primary-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-n8n-dark-50">
                    Configure Step
                  </h2>
                  <span className="text-sm text-n8n-dark-400">{editingStep.name}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowStepEditor(false);
                  setEditingStep(null);
                }}
                className="text-n8n-dark-400 hover:text-n8n-dark-200 transition-all duration-200 p-2 rounded-lg hover:bg-n8n-dark-700/50"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(75vh-90px)]">
              <StepEditor
                step={editingStep}
                onUpdate={handleStepUpdate}
                isConstant={CONSTANT_STEPS.some(cs => cs.name === editingStep.name)}
                workflow={safeWorkflow}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sharp Basic Info Editor Modal */}
      {showBasicInfoEditor && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-n8n-dark-800/95 backdrop-blur-xl rounded-xl border border-n8n-dark-600/50 w-full max-w-4xl max-h-[75vh] overflow-hidden shadow-2xl my-4">
            <div className="p-6 border-b border-n8n-dark-600/50 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-n8n-primary-500/20 to-n8n-primary-600/20 rounded-lg">
                  <Settings size={20} className="text-n8n-primary-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-n8n-dark-50">
                    Workflow Configuration
                  </h2>
                  <p className="text-sm text-n8n-dark-400">Configure workflow settings and parameters</p>
                </div>
              </div>
              <button
                onClick={() => setShowBasicInfoEditor(false)}
                className="text-n8n-dark-400 hover:text-n8n-dark-200 transition-all duration-200 p-2 rounded-lg hover:bg-n8n-dark-700/50"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(75vh-90px)]">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Workflow Name
                    </label>
                    <input
                      type="text"
                      value={workflow.workflow.name}
                      onChange={(e) => handleWorkflowInfoUpdate('name', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="Enter workflow name"
                    />
                  </div>
                {/* Sharp Intent Section */}
                <div className="mt-2 bg-gradient-to-br from-n8n-dark-700/40 to-n8n-dark-600/40 border border-n8n-dark-600/50 rounded-xl p-4 backdrop-blur-sm">
                  <div className="text-sm font-bold text-n8n-dark-50 mb-3 flex items-center space-x-2">
                    <Zap size={14} className="text-n8n-primary-400" />
                    <span>Intent Configuration</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-n8n-dark-300 mb-2">Intent ID</label>
                      <input
                        type="text"
                        value={workflow.intent?.id ?? ''}
                        onChange={(e) => handleIntentUpdate('id', e.target.value)}
                        className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                        placeholder="${TRANSACTION_INTENT_ID}"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-n8n-dark-300 mb-2">Signature</label>
                      <input
                        type="text"
                        value={workflow.intent?.signature ?? ''}
                        onChange={(e) => handleIntentUpdate('signature', e.target.value)}
                        className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                        placeholder="${USER_SIGNATURE}"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-n8n-dark-300 mb-2">Deadline</label>
                      <input
                        type="text"
                        value={workflow.intent?.deadline ?? ''}
                        onChange={(e) => handleIntentUpdate('deadline', e.target.value)}
                        className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                        placeholder="${TRANSACTION_INTENT_DEADLINE}"
                      />
                    </div>
                  </div>
                </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Version
                    </label>
                    <input
                      type="text"
                      value={workflow.workflow.version}
                      onChange={(e) => handleWorkflowInfoUpdate('version', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="e.g., v1.0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Sender Address
                    </label>
                    <input
                      type="text"
                      value={workflow.sender}
                      onChange={(e) => handleBasicInfoUpdate('sender', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="0x..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Execution Fee (ETH)
                    </label>
                    <input
                      type="text"
                      value={workflow.executionFee}
                      onChange={(e) => handleBasicInfoUpdate('executionFee', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Attestor
                    </label>
                    <input
                      type="text"
                      value={workflow.attestor ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('attestor', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="ipfs://... or https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Delegate
                    </label>
                    <input
                      type="text"
                      value={workflow.delegate ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('delegate', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="0x..."
                    />
                  </div>
                  <div className="flex items-center space-x-2 mt-6">
                    <input
                      id="sponsorExecutionFee"
                      type="checkbox"
                      className="accent-n8n-primary-500"
                      checked={Boolean(workflow.sponsor_execution_fee)}
                      onChange={(e) => handleBasicInfoUpdate('sponsor_execution_fee', e.target.checked)}
                    />
                    <label htmlFor="sponsorExecutionFee" className="text-sm text-n8n-dark-300">Sponsor Execution Fee</label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Chain ID
                    </label>
                    <input
                      type="number"
                      value={workflow.chain_id ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('chain_id', Number(e.target.value) || undefined)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="11155111"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      RPC URL
                    </label>
                    <input
                      type="text"
                      value={workflow.rpc_url ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('rpc_url', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Bundler URL
                    </label>
                    <input
                      type="text"
                      value={workflow.bundler_url ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('bundler_url', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="https://api.pimlico.io/..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Paymaster URL
                    </label>
                    <input
                      type="text"
                      value={workflow.paymaster_url ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('paymaster_url', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="https://api.pimlico.io/..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Gas Limit
                    </label>
                    <input
                      type="text"
                      value={workflow.gas_limit ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('gas_limit', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="100000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Max Fee Per Gas
                    </label>
                    <input
                      type="text"
                      value={workflow.max_fee_per_gas ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('max_fee_per_gas', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="20000000000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-n8n-dark-300 mb-2">
                      Max Priority Fee Per Gas
                    </label>
                    <input
                      type="text"
                      value={workflow.max_priority_fee_per_gas ?? ''}
                      onChange={(e) => handleBasicInfoUpdate('max_priority_fee_per_gas', e.target.value)}
                      className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                      placeholder="2000000000"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sharp DApp Config Editor Modal */}
      {showDAppConfigEditor && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-n8n-dark-800/95 backdrop-blur-xl rounded-xl border border-n8n-dark-600/50 w-full max-w-3xl max-h-[75vh] overflow-hidden shadow-2xl my-4">
            <div className="p-6 border-b border-n8n-dark-600/50 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-n8n-accent-500/20 to-n8n-accent-600/20 rounded-lg">
                  <Settings size={20} className="text-n8n-accent-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-n8n-dark-50">
                    Contract Configuration
                  </h2>
                  <p className="text-sm text-n8n-dark-400">Configure target contract and function parameters</p>
                </div>
              </div>
              <button
                onClick={() => setShowDAppConfigEditor(false)}
                className="text-n8n-dark-400 hover:text-n8n-dark-200 transition-all duration-200 p-2 rounded-lg hover:bg-n8n-dark-700/50"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(75vh-90px)]">
              <div className="space-y-6">
                {/* Sharp Basic dApp Info */}
                <div className="bg-gradient-to-br from-n8n-dark-700/40 to-n8n-dark-600/40 border border-n8n-dark-600/50 rounded-xl p-6 backdrop-blur-sm">
                  <h3 className="text-lg font-bold text-n8n-dark-50 mb-4 flex items-center space-x-2">
                    <Settings size={16} className="text-n8n-accent-400" />
                    <span>Basic Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-n8n-dark-300 mb-2">DApp Name</label>
                      <input
                        type="text"
                        value={workflow.workflow.name}
                        readOnly
                        className="w-full px-3 py-2 bg-n8n-dark-600 border border-n8n-dark-600 rounded-n8n text-n8n-dark-400 cursor-not-allowed transition-all duration-200"
                        placeholder="Synced with workflow name"
                      />
                      <p className="text-xs text-n8n-dark-400 mt-1">Auto-synced with workflow name</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-n8n-dark-300 mb-2">Target Chain ID</label>
                      <input
                        type="number"
                        value={typeof workflow.dapp?.targetChainID !== 'undefined' ? workflow.dapp?.targetChainID : (workflow.chain_id ?? 1)}
                        onChange={(e) => handleDAppConfigUpdate('targetChainID', parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                        placeholder="11155111"
                      />
                    </div>
                  </div>
                </div>

                {/* Sharp Target Contract Configuration */}
                <div className="bg-gradient-to-br from-n8n-dark-700/40 to-n8n-dark-600/40 border border-n8n-dark-600/50 rounded-xl p-6 backdrop-blur-sm">
                  <h3 className="text-lg font-bold text-n8n-dark-50 mb-4 flex items-center space-x-2">
                    <Zap size={16} className="text-n8n-success-400" />
                    <span>Target Contract</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-n8n-dark-300 mb-2">Contract Address</label>
                      <input
                        type="text"
                        value={workflow.dapp?.targetContractAddress ?? ''}
                        onChange={(e) => {
                          handleDAppConfigUpdate('targetContractAddress', e.target.value);
                          handleTargetUpdate('contract', e.target.value);
                        }}
                        className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                        placeholder="{{ENV.TARGET_CONTRACT}} or 0x..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-n8n-dark-300 mb-2">Function Signature</label>
                      <input
                        type="text"
                        value={workflow.dapp?.targetFunctionSignature ?? ''}
                        onChange={(e) => {
                          handleDAppConfigUpdate('targetFunctionSignature', e.target.value);
                          handleFunctionSignatureChange(e.target.value);
                        }}
                        className="w-full px-3 py-2 bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n text-n8n-dark-200 placeholder-n8n-dark-400 focus:outline-none focus:ring-2 focus:ring-n8n-primary-500 focus:border-n8n-primary-500 transition-all duration-200"
                        placeholder="submitData((uint256,uint256,bytes32,(bytes32,bytes,bytes)[],bytes,bool,bytes),uint256,address)"
                      />
                      <p className="text-xs text-n8n-dark-400 mt-1">
                        Parameters beyond the first AuthData tuple are automatically detected
                      </p>
                    </div>

                    {/* Extra Parameters */}
                    <div>
                      <div className="mb-3">
                        <label className="text-sm font-medium text-n8n-dark-300">Extra Parameters</label>
                        <p className="text-xs text-n8n-dark-400 mt-1">
                          Automatically detected from function signature. Customize names and provide values.
                        </p>
                      </div>
                      
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {(workflow.target?.parameters || []).map((param: any, index: number) => (
                          <div key={index} className="bg-n8n-dark-800/50 border border-n8n-dark-600 rounded p-3">
                            <div className="grid grid-cols-12 gap-2 items-center">
                              <div className="col-span-3">
                                <input
                                  type="text"
                                  placeholder={`param${index + 1}`}
                                  value={param.name || ''}
                                  onChange={(e) => {
                                    const newParams = [...(workflow.target?.parameters || [])];
                                    newParams[index] = { ...newParams[index], name: e.target.value };
                                    handleTargetUpdate('parameters', newParams);
                                  }}
                                  className="w-full px-2 py-1 bg-n8n-dark-700 border border-n8n-dark-600 rounded text-sm text-n8n-dark-200"
                                />
                                <div className="text-xs text-n8n-dark-400 mt-1">Name</div>
                              </div>
                              <div className="col-span-2">
                                <div className="px-2 py-1 bg-n8n-dark-900 border border-n8n-dark-600 rounded text-sm text-n8n-dark-300">
                                  {param.type || 'string'}
                                </div>
                                <div className="text-xs text-n8n-dark-400 mt-1">Type</div>
                              </div>
                              <div className="col-span-7">
                                <input
                                  type="text"
                                  placeholder="Enter value or ${variable}"
                                  value={param.value || ''}
                                  onChange={(e) => {
                                    const newParams = [...(workflow.target?.parameters || [])];
                                    const detectedType = detectParameterType(e.target.value);
                                    newParams[index] = { 
                                      ...newParams[index], 
                                      value: e.target.value,
                                      type: param.type === 'string' ? detectedType : param.type
                                    };
                                    handleTargetUpdate('parameters', newParams);
                                  }}
                                  className="w-full px-2 py-1 bg-n8n-dark-700 border border-n8n-dark-600 rounded text-sm text-n8n-dark-200"
                                />
                                <div className="text-xs text-n8n-dark-400 mt-1">Value</div>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {(!workflow.target?.parameters || workflow.target.parameters.length === 0) && (
                          <div className="text-sm text-n8n-dark-400 italic text-center py-4 border-2 border-dashed border-n8n-dark-600 rounded">
                            No extra parameters. Parameters beyond AuthData will appear here when you enter a function signature.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Wrapper component to provide ReactFlowProvider
const N8nStyleCanvasWrapper: React.FC<N8nStyleCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <N8nStyleCanvas {...props} />
    </ReactFlowProvider>
  );
};

export default N8nStyleCanvasWrapper;